import React, { useEffect, useRef } from 'react';
import { Zap, TrendingUp, Clock, FileText, CheckCircle, ChevronDown } from 'lucide-react';
import { motion } from 'framer-motion';
import WhatsAppMockup from './WhatsAppMockup';

const HeroSection = () => {
  const mockupRef = useRef(null);
  const backgroundRef = useRef(null);

  // Parallax scroll effect
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (mediaQuery.matches) return;

    const handleScroll = () => {
      const scrolled = window.scrollY;
      const mockupOffset = Math.min(scrolled * 0.2, 120);
      const backgroundOffset = Math.min(scrolled * 0.08, 80);

      if (mockupRef.current) {
        mockupRef.current.style.setProperty('--mockup-parallax', `${mockupOffset}px`);
      }
      if (backgroundRef.current) {
        backgroundRef.current.style.setProperty('--background-parallax', `${backgroundOffset}px`);
      }
    };

    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // ========================================
  // 🔧 SCROLL FIX - Canlı Senaryolar Section
  // ========================================
  const handleScenarioScroll = () => {
    const possibleIds = [
      'canli-senaryolar',
      'live-scenarios',
      'scenarios',
      'demo-scenarios',
      'whatsapp-scenarios',
    ];

    let target = null;
    for (const id of possibleIds) {
      target = document.getElementById(id);
      if (target) {
        console.log(`✅ Section bulundu: #${id}`);
        break;
      }
    }

    if (target) {
      const headerOffset = 80;
      const elementPosition = target.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth',
      });
    } else {
      console.warn("⚠️ Canlı senaryolar section bulunamadı. Lütfen HTML'de section ID'sini kontrol edin.");
      console.log('Aranan IDler:', possibleIds);
    }
  };

  // ========================================
  // 🎬 FRAMER MOTION ANIMATION VARIANTS
  // ========================================
  const containerVariants = {
    hidden: { opacity: 1 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.3,
      },
    },
  };

  const cardVariants = {
    hidden: {
      opacity: 0,
      scale: 0.85,
      y: 30,
    },
    visible: {
      opacity: 1,
      scale: 1,
      y: 0,
      transition: {
        type: 'spring',
        stiffness: 100,
        damping: 15,
        duration: 0.6,
      },
    },
  };

  const hoverVariants = {
    scale: 1.05,
    y: -4,
    boxShadow: '0 12px 24px rgba(16, 185, 129, 0.25)',
    transition: {
      type: 'spring',
      stiffness: 400,
      damping: 20,
    },
  };

  const benefits = [
    {
      icon: '⏱️',
      text: "Tekrarlayan mesajlarda %40'a kadar zaman tasarrufu",
      gradient: 'from-emerald-50 to-teal-50',
    },
    {
      icon: '📦',
      text: 'İade & kargo süreçlerinde daha az şikâyet',
      gradient: 'from-blue-50 to-cyan-50',
    },
    {
      icon: '📊',
      text: 'Ücretsiz, 3-4 sayfalık detaylı Check-Up raporu',
      gradient: 'from-purple-50 to-pink-50',
    },
  ];

  return (
    <section
      className="relative overflow-x-hidden bg-gradient-to-b from-emerald-50/80 via-white to-emerald-50/40 pt-0"
      aria-labelledby="hero-title"
    >
      {/* Arka plan efektleri – animasyonlu çemberler */}
      <div className="hero-background-effects" ref={backgroundRef} aria-hidden="true">
        <div
          className="hero-bg-circle hero-bg-circle-primary"
          style={{
            top: '-15%',
            left: '-10%',
            width: '800px',
            height: '800px',
            background: 'radial-gradient(circle, rgba(16,185,129,0.15) 0%, transparent 70%)',
            filter: 'blur(120px)',
            animation: 'rotateCW 30s linear infinite',
            willChange: 'transform',
          }}
        />
        <div
          className="hero-bg-circle hero-bg-circle-secondary"
          style={{
            bottom: '-20%',
            right: '-5%',
            width: '700px',
            height: '700px',
            background: 'radial-gradient(circle, rgba(6,182,212,0.12) 0%, transparent 70%)',
            filter: 'blur(100px)',
            animation: 'rotateCCW 35s linear infinite',
            willChange: 'transform',
          }}
        />
        <div className="mesh-blob" />
      </div>

      {/* Ana içerik container */}
      <div className="relative max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 pt-4 pb-4 md:pt-6 md:pb-6 lg:pt-8 lg:pb-8 -mt-4 md:-mt-6 lg:-mt-8 overflow-hidden">
        
        {/* Grid: 2 kolon desktop, 1 kolon mobile */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8 lg:gap-10 items-start w-full">
          
          {/* SOL KOLON – Metin bloğu */}
          <div className="flex flex-col gap-4 lg:gap-5 text-center lg:text-left pt-0 w-full justify-start">
            
            {/* 1. Badge */}
            <p className="hero-pill mx-auto lg:mx-0 mt-0">
              <span className="w-2 h-2 rounded-full bg-emerald-500 mr-2 inline-flex" aria-hidden="true"></span>
              ÜCRETSİZ 360° OTOMASYON CHECK-UP'ı
              <span className="ml-2 text-slate-400 hidden sm:inline">
                WhatsApp tabanlı akıllı otomasyon
              </span>
            </p>

            {/* 2. H1 Başlık */}
            <h1 id="hero-title" className="hero-heading">
              WhatsApp Mesajlarınızı
              <br />
              <span className="relative inline-block mt-1">
                <span className="text-emerald-500">Otomatik SATIŞA</span>
                <span className="underline-glow"></span>
              </span>{' '}
              Çevirin
            </h1>

            {/* 3. Kısa Açıklama */}
            <p className="hero-subtitle">
              Akıllı senaryolar ile müşteri sorularını yanıtlayın, randevu alın ve <strong>7/24 satış yapın.</strong>
            </p>

            {/* 4. CTA Bloğu */}
            <div className="flex flex-col items-center lg:items-start gap-3 w-full">
              {/* Primary CTA */}
              <a
                href="https://calendly.com/otomasyoncevik/30min"
                target="_blank"
                rel="noopener noreferrer"
                className="hero-cta hero-cta-primary w-full md:w-auto uppercase tracking-wide justify-center"
              >
                <span className="text-2xl" role="img" aria-hidden="true">
                  🔥
                </span>
                ÜCRETSİZ CHECK-UP RANDEVUSU AL
              </a>

              {/* Urgency metni */}
              <p className="hero-scarcity text-red-600 text-sm font-semibold animate-pulse">
                ⏰ Bu hafta sadece 8 slot kaldı!
              </p>

              {/* Secondary CTA */}
              <button
                type="button"
                className="hero-cta hero-cta-secondary w-full md:w-auto justify-center"
                onClick={handleScenarioScroll}
              >
                🎬 Canlı Senaryo Örnekleri Gör
              </button>
            </div>

            {/* Value Proposition Stack */}
            <motion.div
              className="mt-6 sm:mt-8 space-y-4 lg:space-y-6 w-full"
              variants={containerVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: '-50px' }}
            >
              {/* 1. Ana Başlık Card (Cyan/Blue Theme) */}
              <motion.div
                variants={cardVariants}
                className="bg-gradient-to-br from-cyan-50 to-blue-50 rounded-2xl p-4 sm:p-6 lg:p-8 border-2 border-cyan-200 shadow-sm w-full"
              >
                <div className="flex items-start gap-3 mb-4">
                  <div className="w-12 h-12 bg-cyan-500 rounded-xl flex items-center justify-center flex-shrink-0">
                    <CheckCircle className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl lg:text-2xl font-bold text-gray-900 leading-tight">
                      360° Gelir & Müşteri Deneyimi Performans Check-Up'ı
                    </h3>
                    <p className="text-sm lg:text-base text-gray-700 mt-2 leading-relaxed">
                      WhatsApp, Instagram ve Web Chat trafiğinizdeki gizli gelir fırsatlarını ortaya çıkarıyoruz.
                    </p>
                  </div>
                </div>

                {/* Process flow */}
                <div className="flex flex-wrap items-center justify-center gap-2 sm:gap-3 text-xs lg:text-sm text-gray-700 mt-4 sm:mt-6 bg-white/60 rounded-lg p-3 sm:p-4">
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4 text-cyan-600" />
                    <span className="font-medium">20 dakikalık mini analiz görüşmesi</span>
                  </div>
                  <span className="text-cyan-400">→</span>
                  <div className="flex items-center gap-2">
                    <FileText className="w-4 h-4 text-cyan-600" />
                    <span className="font-medium">3-5 sayfalık kişisel rapor</span>
                  </div>
                  <span className="text-cyan-400">→</span>
                  <div className="flex items-center gap-2">
                    <TrendingUp className="w-4 h-4 text-cyan-600" />
                    <span className="font-medium">Somut gelir & tasarruf potansiyeli</span>
                  </div>
                </div>
              </motion.div>

              {/* 2. Process Steps Mini Cards */}
              <div className="space-y-3">
                {/* Step 1 - Orange */}
                <motion.div
                  variants={cardVariants}
                  className="bg-white rounded-xl p-3 sm:p-4 lg:p-5 border-l-4 border-orange-400 shadow-sm hover:shadow-md transition-all duration-300 hover:-translate-x-1 w-full"
                >
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <span className="text-orange-600 font-bold text-sm">1</span>
                    </div>
                    <div className="flex-1">
                      <h4 className="font-semibold text-gray-900 mb-1 text-sm lg:text-base">
                        Gözden Kaçan Gelir Fırsatları
                      </h4>
                      <p className="text-xs lg:text-sm text-gray-600 leading-relaxed">
                        Cevapsız mesajlar, kaçırılan satışlar ve boşa giden reklam bütçesini analiz ediyoruz.
                      </p>
                    </div>
                  </div>
                </motion.div>

                {/* Step 2 - Purple */}
                <motion.div
                  variants={cardVariants}
                  className="bg-white rounded-xl p-3 sm:p-4 lg:p-5 border-l-4 border-purple-400 shadow-sm hover:shadow-md transition-all duration-300 hover:-translate-x-1 w-full"
                >
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <span className="text-purple-600 font-bold text-sm">2</span>
                    </div>
                    <div className="flex-1">
                      <h4 className="font-semibold text-gray-900 mb-1 text-sm lg:text-base">
                        Size Özel Otomasyon Yol Haritası
                      </h4>
                      <p className="text-xs lg:text-sm text-gray-600 leading-relaxed">
                        Markanızın diline uygun, dönüşüm odaklı WhatsApp–Instagram–Web akışları tasarlıyoruz.
                      </p>
                    </div>
                  </div>
                </motion.div>
              </div>
            </motion.div>

          </div>

          {/* SAĞ KOLON – WhatsApp Mockup + IŞIN EFEKTLERİ */}
          <div className="order-2 lg:order-2 flex justify-center lg:justify-end items-center w-full px-4 sm:px-6 lg:px-0">
            <div
              className="hero-mockup-shell-wrapper relative w-full max-w-[min(350px,90vw)] sm:max-w-[min(380px,85vw)] md:max-w-sm lg:max-w-md mx-auto lg:mx-0 lg:ml-auto"
              ref={mockupRef}
            >
              <div className="phone-rays-outer" aria-hidden="true"></div>
              <div className="phone-rays-inner" aria-hidden="true"></div>
              <div className="phone-glow-pulse" aria-hidden="true"></div>

              <div className="hero-mockup-shell relative z-10 w-full">
                <div className="mockup-shine" aria-hidden="true"></div>
                <WhatsAppMockup type="hero" />
              </div>

              {/* Signature slogan under mockup */}
              <div className="mt-6 sm:mt-8 text-center max-w-xl mx-auto px-2 sm:px-4">
                <p className="text-base sm:text-lg md:text-2xl leading-relaxed text-gray-700 flex flex-col items-center gap-1">
                  <span className="block">Her işletme için</span>
                  <span className="signature-text block">kişiselleştirilmiş</span>
                  <span className="block">otomasyon planı.</span>
                </p>
              </div>

              {/* ROI Impact Card (Emerald Theme) - Sağ kolona taşındı */}
              <motion.div
                variants={cardVariants}
                className="mt-6 sm:mt-8 bg-gradient-to-br from-emerald-50 via-teal-50 to-green-50 rounded-2xl p-4 sm:p-5 lg:p-6 border-2 border-emerald-200 shadow-md w-full"
              >
                <div className="flex items-start gap-3 sm:gap-4">
                  <div className="w-12 h-12 lg:w-14 lg:h-14 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg">
                    <TrendingUp className="w-6 h-6 lg:w-7 lg:h-7 text-white" />
                  </div>
                  <div className="flex-1">
                    <p className="text-base lg:text-lg font-bold text-gray-900 mb-3 leading-tight">
                      Markaların otomasyonla elde ettiği sonuçlar
                    </p>
                    <ul className="space-y-3">
                      <li className="flex items-start gap-3 text-sm lg:text-base text-gray-800">
                        <div className="w-5 h-5 bg-emerald-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                          <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                          </svg>
                        </div>
                        <span className="leading-relaxed">
                          <strong className="text-emerald-700 font-bold">1.200+ mesajın</strong> otomatik yönetimi
                        </span>
                      </li>
                      <li className="flex items-start gap-3 text-sm lg:text-base text-gray-800">
                        <div className="w-5 h-5 bg-emerald-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                          <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                          </svg>
                        </div>
                        <span className="leading-relaxed">
                          <strong className="text-emerald-700 font-bold">%60'a varan</strong> cevap süresi düşüşü
                        </span>
                      </li>
                      <li className="flex items-start gap-3 text-sm lg:text-base text-gray-800">
                        <div className="w-5 h-5 bg-emerald-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                          <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                          </svg>
                        </div>
                        <span className="leading-relaxed">
                          <strong className="text-emerald-700 font-bold">₺15.000–₺120.000</strong> arası ek gelir / tasarruf
                        </span>
                      </li>
                      <li className="flex items-start gap-3 text-sm lg:text-base text-gray-800">
                        <div className="w-5 h-5 bg-emerald-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                          <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                          </svg>
                        </div>
                        <span className="leading-relaxed">
                          <strong className="text-emerald-700 font-bold">%25–40</strong> daha yüksek reklam dönüşümü
                        </span>
                      </li>
                    </ul>
                    {/* CTA Hint */}
                    <div className="mt-4 pt-4 border-t border-emerald-200">
                      <p className="text-xs lg:text-sm text-gray-600 italic">
                        Potansiyelinizi 20 dakikalık ücretsiz Check-Up'ta öğrenin.
                      </p>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>

        </div>
      </div>

      {/* Stats bar */}
      <div className="mt-2 md:mt-3">
        <div className="stats-bar flex flex-wrap items-center justify-center gap-x-6 gap-y-3 text-base sm:text-lg text-slate-600">
          <span className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-emerald-500" aria-hidden="true" />
            %40 satış artışı
          </span>
          <span className="hidden sm:inline text-slate-400" aria-hidden="true">
            •
          </span>
          <span className="flex items-center gap-2">
            <Zap className="h-5 w-5 text-emerald-500" aria-hidden="true" />
            7/24 otomatik yanıt
          </span>
        </div>
      </div>

      {/* Feature highlight cards */}
      <motion.div
        className="mt-4 md:mt-6 lg:mt-6 grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4 w-full px-4 md:px-0"
        variants={containerVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: '-100px' }}
      >
        {benefits.map((benefit, index) => (
          <motion.div
            key={index}
            variants={cardVariants}
            whileHover={hoverVariants}
            className={`
              relative
              flex flex-col items-center justify-center
              px-6 py-4
              min-h-[90px]
              bg-white/80
              border border-emerald-100
              rounded-2xl
              shadow-sm
              cursor-pointer
              overflow-hidden
              backdrop-blur-sm
              group
              benefit-card-animated
              w-full
            `}
            style={{ willChange: 'transform' }}
          >
            <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
              <div className="absolute inset-0 rounded-2xl border-2 border-transparent bg-gradient-to-r from-emerald-400 via-teal-400 to-emerald-400 bg-clip-border animate-border-flow" />
            </div>

            <div className="relative z-10 flex flex-col items-center justify-center text-center gap-2">
              <span className="text-2xl flex-shrink-0 benefit-icon" role="img" aria-hidden="true">
                {benefit.icon}
              </span>
              <p className="text-sm md:text-base font-medium text-slate-700 text-center leading-relaxed">
                {benefit.text}
              </p>
            </div>

            <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700">
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />
            </div>
          </motion.div>
        ))}
      </motion.div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce hidden md:block" aria-hidden="true">
        <ChevronDown className="w-6 h-6 text-gray-400 opacity-60" />
      </div>

      {/* Centered Pills */}
      <div className="mt-12 w-full">
        <div className="pills-container">
          <div className="pill-glow-layer" aria-hidden="true"></div>
          {['E-ticaret Markaları', 'Spor Salonları', 'SPA & Wellness', 'Oteller'].map(label => (
            <span key={label} className="sector-pill">
              <span className="sector-pill-icon" aria-hidden="true">
                ●
              </span>
              {label}
            </span>
          ))}
        </div>
      </div>
    </section>
  );
};

export default HeroSection;